/**
 * What the exercise card's foot says about an exercise's progression: for each stored rule, the
 * KIND it is and the FIELD it writes.
 *
 * ── The line this module used to refuse to cross, and why it moved ───────────────────────────
 * Until 9 September 2026 this file read only the map's KEYS and stated the axes, on the argument
 * that opening a rule document would be "the port, done informally, without the parity fixture that
 * is supposed to gate it". That argument was applied one step too wide, and the owner's ask
 * ("pokazywało czy jest statyczna czy dynamiczna i na jakim polu") is the correction.
 *
 * What is frozen is the PROJECTION — the engine that computes what a rule will produce in cycle
 * three, which is the thing that needs a fixture the backend emits and this app replays, because it
 * is a calculation two codebases have to agree on. Reading `type` off a document that already
 * arrived is not a calculation and has nothing to agree with: `Static` means static, and there is no
 * second opinion to be wrong about. `delta`, `deltaUnit` and `banded` stay unread here, because a
 * printed increment IS a claim about what the engine will do with it.
 *
 * The map is still held opaque everywhere else — `ExerciseDraft.progressionRules` is read once and
 * emitted byte-identical, and nothing in this file writes to it.
 *
 * ── Why the axes are worth naming, given the app cannot compute the rules ────────────────────
 * The failure this closes is a real gesture with a silent consequence. Duplicating a day copies its
 * rules BY VALUE — the copy really does progress at +2.5 kg a cycle — and under one uniform
 * paragraph a trainer could not tell the copies apart from an exercise with no rule at all.
 *
 * No React, no DOM, no query hook, no i18next: `t` is a parameter.
 */

import {
  bandedGateOf,
  carriesProgression,
  ruleFor,
  type ExerciseDraft,
  type ProgressionRules,
} from '~/entities/plan-draft';
import { sortMeasurementFields, type MeasurementFieldKey } from '~/shared/measurement';

import type { Translate } from './matrixCellState';

/** Where the measurement-field labels live — the set table's own vocabulary, not a second copy. */
const FIELD_KEY = 'web:planBuilder.day.field';
const KEY = 'web:planBuilder.progression';

/**
 * Which of the mock's `PROGC` hues a kind chip takes. `null` for a kind this build has no word for,
 * which is drawn neutral rather than given a colour nobody chose for it.
 */
export type RuleKindTone = 'static' | 'dynamic' | null;

/** One stored rule, as the foot prints it: a kind chip and the field beside it. */
export interface RuleAxisView {
  /** The field's wire name. Stable, and unique within a map, so it is the React key. */
  readonly key: string;
  /**
   * `Statyczna` / `Dynamiczna`, or the word the SERVER used for a kind this build cannot name.
   *
   * EMPTY where the document carries no `type` at all. Malformed, and still an exercise that
   * progresses, so the field is named on its own rather than beside a chip with nothing in it.
   */
  readonly kind: string;
  readonly tone: RuleKindTone;
  /** `Ciężar`, `Powt.`, or the name the server spelled for a field this build has no word for. */
  readonly field: string;
}

export interface RuleStatementView {
  /**
   * One entry per stored rule, in the field dictionary's order. Empty when nothing progresses.
   *
   * Ordered rather than left as the server serialised it, for the reason R4 gives about every other
   * field list in this app: two exercises carrying the same two rules must read the same, or the
   * difference between them looks like a difference in the plan.
   */
  readonly axes: readonly RuleAxisView[];
  /** What the tile prints instead, when `axes` is empty — "Bez progresji". */
  readonly noRule: string;
  /**
   * The whole state as one sentence — the tile's `title` and its accessible name.
   *
   * The chips are uppercase, tracked and clipped to the tile's own width: a screen reader must hear
   * the sentence rather than the abbreviations, and a trainer whose row ellipsised must be able to
   * hover it.
   */
  readonly statement: string;
}

/** The two kinds a rule document carries on the wire, and the word each one reads as. */
const KIND_WORDS: Readonly<Record<string, { readonly key: string; readonly tone: RuleKindTone }>> = {
  Static: { key: `${KEY}.family.static.name`, tone: 'static' },
  PreviousExecution: { key: `${KEY}.family.dynamic.name`, tone: 'dynamic' },
};

/**
 * The statement for one exercise — its READABLE rules, then the entries this build has no reading
 * for. The two arrive as separate arguments rather than as one object because each is a stable
 * reference on the exercise node and a store selector minting a pair per call would report a changed
 * snapshot forever.
 *
 * An absent map and an empty one answer the same, and they mean the same to the backend: no rule is
 * stored, so nothing progresses. A key the wire named and this build has no field for is listed by
 * the name the SERVER spelled rather than dropped — the library table's own precedent for an
 * unrecognised enum. Dropping it would let the statement say "no progression" over an exercise that
 * has one, which is the exact lie this whole surface exists to avoid. A rule KIND this build has no
 * word for is treated the same way, and for the same reason.
 */
export function ruleStatement(
  rules: ProgressionRules,
  unreadable: Readonly<Record<string, unknown>>,
  t: Translate,
): RuleStatementView {
  const axes = ruleAxes(rules, unreadable, t);
  const noRule = t(`${KEY}.noRule`);

  return {
    axes,
    noRule,
    statement:
      axes.length === 0
        ? noRule
        : t(`${KEY}.ruleSaved`, { fields: axes.map(axisWords).join(', ') }),
  };
}

/** One axis as a phrase — the kind, then the field. Just the field where there is no kind to name. */
function axisWords(axis: RuleAxisView): string {
  return axis.kind === '' ? axis.field : `${axis.kind} · ${axis.field}`;
}

function ruleAxes(
  rules: ProgressionRules,
  unreadable: Readonly<Record<string, unknown>>,
  t: Translate,
): RuleAxisView[] {
  const named = sortMeasurementFields(Object.keys(rules) as MeasurementFieldKey[]).map((field) => ({
    key: field,
    field: t(`${FIELD_KEY}.${field}`),
    ...kindOf(rules[field]?.type, t),
  }));

  // Then the entries this build has no reading for, named as the SERVER spelled them. Dropping one
  // would let the row say "Bez progresji" over an exercise that has a rule — the exact lie this
  // surface exists to avoid — and giving it a hue would claim a kind nobody here can name. The
  // library table's own precedent for an unrecognised enum, and the reason `unreadable` exists.
  const foreign = Object.entries(unreadable).map(([wireField, document]) => ({
    key: wireField,
    field: wireField,
    kind: wireKindOf(document),
    tone: null,
  }));

  return [...named, ...foreign];
}

/**
 * The `type` a document this build cannot read carries, or an empty string when it carries none at
 * all. Malformed, and still an exercise that progresses, so the field is named on its own rather
 * than beside a chip with nothing in it.
 */
function wireKindOf(document: unknown): string {
  if (typeof document !== 'object' || document === null) return '';
  const type: unknown = (document as { readonly type?: unknown }).type;
  return typeof type === 'string' ? type : '';
}

/**
 * A document's kind, read from `type` alone.
 *
 * Widened to `string` on purpose: the generated union has two members today and the backend may ship
 * a third to a client that cannot be force-updated, so a kind this build has never heard of has to
 * print as itself rather than fall out of the row.
 */
function kindOf(
  type: string | undefined,
  t: Translate,
): { readonly kind: string; readonly tone: RuleKindTone } {
  const word = KIND_WORDS[type ?? ''];
  return word === undefined ? { kind: type ?? '', tone: null } : { kind: t(word.key), tone: word.tone };
}

/* ── A change to one exercise under review (board U) ───────────────────────────────────────── */

/** The two rule maps an exercise carries: the ones this build reads, and the ones it does not. */
export interface StoredRules {
  readonly progressionRules: ProgressionRules;
  readonly unreadableProgressionRules: Readonly<Record<string, unknown>>;
}

/** The increment each side states („+2,5 kg / cykl"), worded by the proposal; `null` for none. */
export interface RuleIncrements {
  readonly before: string | null;
  readonly after: string | null;
}

export interface RuleChangeView {
  readonly now: RuleStatementView;
  /** The rule the exercise had, drawn struck: `null` where the change does not move it. */
  readonly was: RuleStatementView | null;
  /** The row as one sentence for a listener: „było …, jest …" when it moved, the rule otherwise. */
  readonly spoken: string;
}

function sentenceOf(view: RuleStatementView, increment: string | null): string {
  return increment === null ? view.statement : `${view.statement} ${increment}`;
}

/**
 * The progression row of a change under review. The rule MOVED when its statement or its increment
 * differs: the statement names no increment, so a change of increment alone („+2,5" to „+5 kg /
 * cykl") would otherwise read as no change at all.
 */
export function ruleChangeView(
  after: StoredRules,
  before: StoredRules | null,
  increments: RuleIncrements,
  t: Translate,
): RuleChangeView {
  const now = ruleStatement(after.progressionRules, after.unreadableProgressionRules, t);
  const previous = before === null ? null : ruleStatement(before.progressionRules, before.unreadableProgressionRules, t);
  const moved = previous !== null && (previous.statement !== now.statement || increments.before !== increments.after);
  return {
    now,
    was: moved ? previous : null,
    spoken: moved
      ? t(`${KEY}.changed`, { before: sentenceOf(previous, increments.before), after: sentenceOf(now, increments.after) })
      : sentenceOf(now, increments.after),
  };
}

/* ── What the SHEET's column chips say, which is the same question one level down ──────────── */

const SHEET_KEY = 'web:planBuilder.progression.sheet';

/** What the dot on a column chip means: its rule's family, that it is another rule's gate, or nothing. */
export type ColumnMark = 'static' | 'fromResult' | 'gate' | null;

/** One column chip's whole reading: the hue its dot takes, and the same thing in words. */
export interface ColumnChipReading {
  readonly mark: ColumnMark;
  /** The i18n key of the state, which trails the chip's visible label in its accessible name. */
  readonly stateKey: string;
}

/**
 * What one column of an exercise is, as the sheet's chips draw it.
 *
 * THREE READINGS AND THEY ARE ORDERED. A column carrying a rule that progresses is that rule's
 * family, whatever else is true of it; a column carrying none may still be the bar another rule's
 * client climbs to; anything else is plain. A rule that carries no progression at all (`Static`
 * with no delta) is NOT a mark — it produces the same numbers a plain column does, and a hue over
 * it would promise a progression the grid does not draw.
 *
 * It lives here rather than in `SheetHead` because it is the same question `ruleStatement` answers
 * for the card's foot, one level down, and because colour is never the only channel: the word under
 * the hue has to come from the same decision that picked it, or the two drift.
 */
export function columnChipReading(
  exercise: Pick<ExerciseDraft, 'progressionRules'>,
  field: MeasurementFieldKey,
): ColumnChipReading {
  const rule = ruleFor(exercise.progressionRules, field);
  if (rule !== null && carriesProgression(rule)) {
    const mark = rule.type === 'Static' ? 'static' : 'fromResult';
    return { mark, stateKey: `${SHEET_KEY}.columnState.ruled` };
  }
  if (bandedGateOf(exercise.progressionRules) === field) {
    return { mark: 'gate', stateKey: `${SHEET_KEY}.columnState.gate` };
  }
  return { mark: null, stateKey: `${SHEET_KEY}.columnState.plain` };
}
